/**
 * Esc peels memory levels only when aimed at the frame or its pocket; a press
 * on the empty workspace (canvas, wall or focus backdrop) closes the pocket.
 */
import { useEffect, type RefObject } from "react";
import { isWorkspacePaneTarget } from "@/lib/workspace-pane";
import { useMemoryStore } from "@/stores/memory-store";

const EDITABLE = "input, textarea, [contenteditable='true']";

export function useMemoryFrameKeys(
  frameRef: RefObject<HTMLElement | null>,
  pocketRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (
        event.key !== "Escape" ||
        event.defaultPrevented ||
        !target ||
        !(
          frameRef.current?.contains(target) ||
          pocketRef.current?.contains(target)
        )
      )
        return;
      const { pocketStack, trail } = useMemoryStore.getState();
      if (
        (target.matches(EDITABLE) && (target as HTMLInputElement).value) ||
        (pocketStack.length === 0 && trail.length < 2)
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
      useMemoryStore.getState().back();
    };
    const onPointerDown = (event: PointerEvent) => {
      const onPane = isWorkspacePaneTarget(event.target);
      if (onPane && useMemoryStore.getState().pocketStack.length > 0)
        useMemoryStore.getState().closePocket();
    };
    window.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [frameRef, pocketRef]);
}
